import { useEffect, useState } from 'react';
import CustomChip from '../../components/CustomChip';
import {
  TbRosetteDiscountCheckFilled,
  TbRosetteDiscountOff,
} from 'react-icons/tb';
import { PackageGet, ServiceGet } from '../../../utils/type';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

type PackageSectionProps = {
  data: ServiceGet;
  onChange: (packageId: string) => void;
};

const PackageSection = ({ data, onChange }: PackageSectionProps) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [packages, setPackages] = useState<PackageGet[]>([]);

  useEffect(() => {
    if (data?.packages) {
      setPackages(data.packages);
    }
  }, [data]);

  // Default select first package
  useEffect(() => {
    if (packages.length > 0) {
      setSelectedPackage(packages[0].packageId);
    }
  }, [packages]);

  // Notify parent
  useEffect(() => {
    if (selectedPackage) {
      onChange(selectedPackage);
    }
  }, [selectedPackage, onChange]);

  // 🔎 discount validity checker
  const isDiscountValid = (discount: any) => {
    if (!discount?.IsActive) return false;
    if (!discount.amount || discount.amount <= 0) return false;

    const now = moment();
    const start = discount?.duration?.start
      ? moment(discount.duration.start)
      : null;
    const end = discount?.duration?.end ? moment(discount.duration.end) : null;

    if (start && now.isBefore(start)) return false; // not started yet
    if (end && now.isAfter(end)) return false; // already expired

    return true;
  };

  return (
    <div className="mt-3 border p-3 rounded-md border-primary/30 bg-gray-50">
      <div className="border-b mb-2 flex justify-between items-center">
        <p className="text-title-bold mb-2">{t('booking.choose_package')}</p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {packages.map((pkg: PackageGet, index: number) => {
          const isSelected = selectedPackage === pkg.packageId;
          const soldOut = pkg.isSoldOut;
          const validDiscount = pkg.discount && isDiscountValid(pkg.discount);

          return (
            <button
              key={index}
              disabled={soldOut}
              onClick={() => setSelectedPackage(pkg.packageId)}
              className={`flex flex-row items-center border p-1.5 rounded-md ${
                soldOut
                  ? 'border-red-300 bg-red-100 cursor-not-allowed opacity-60'
                  : isSelected
                    ? 'border-primary bg-secondary/10'
                    : 'hover-primary-gradient border-primary/30 bg-white'
              }`}
            >
              <div className="flex flex-col text-left ml-3 w-full">
                <div className="flex justify-between items-center w-full">
                  <p className="text-body">{pkg.packageName || '-'}</p>

                  {soldOut ? (
                    <>
                      <CustomChip
                        label={t('booking.sold_out')}
                        color="danger"
                        variant="flat"
                        size="sm"
                        startContent={
                          <TbRosetteDiscountOff className="text-sm" />
                        }
                      ></CustomChip>
                    </>
                  ) : (
                    <>
                      {/* Only show chip if discount is valid */}
                      {validDiscount ? (
                        <CustomChip
                          label={`${
                            pkg.discount.valueType !== 'percentage' ? '$' : ''
                          }${pkg.discount.amount}${
                            pkg.discount.valueType === 'percentage' ? '%' : ''
                          } Save`}
                          color="success"
                          size="sm"
                          variant="flat"
                          startContent={
                            <TbRosetteDiscountCheckFilled className="text-sm" />
                          }
                          classNames={{
                            base: 'text-xs',
                            content: 'text-xs',
                          }}
                        />
                      ) : null}
                    </>
                  )}
                </div>
                <p className="text-body-bold">${pkg.price ?? '-'}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PackageSection;
